import type { MapGeometry } from "../shared/types";

interface Point {
  x: number;
  y: number;
}
interface Segment {
  a: Point;
  b: Point;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}
const EPSILON = 1e-8;
const point = ([x, y]: number[]): Point => ({ x, y });
const cross = (a: Point, b: Point, c: Point) =>
  (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
const segment = (a: Point, b: Point): Segment => ({
  a,
  b,
  minX: Math.min(a.x, b.x),
  minY: Math.min(a.y, b.y),
  maxX: Math.max(a.x, b.x),
  maxY: Math.max(a.y, b.y),
});
function distanceSquared(p: Point, a: Point, b: Point) {
  const dx = b.x - a.x,
    dy = b.y - a.y;
  const length = dx * dx + dy * dy;
  const t = length
    ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / length))
    : 0;
  return (p.x - a.x - t * dx) ** 2 + (p.y - a.y - t * dy) ** 2;
}
function onSegment(p: Point, a: Point, b: Point) {
  return distanceSquared(p, a, b) < EPSILON ** 2;
}
function inRing(p: Point, ring: readonly Point[], boundary: boolean): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j],
      b = ring[i];
    // Most ring edges cannot meet this horizontal ray. Reject them before
    // the distance test, especially collinear subdivisions in imported plans.
    if (
      p.y < Math.min(a.y, b.y) - EPSILON ||
      p.y > Math.max(a.y, b.y) + EPSILON ||
      p.x > Math.max(a.x, b.x) + EPSILON
    )
      continue;
    if (onSegment(p, a, b)) return boundary;
    if (
      a.y > p.y !== b.y > p.y &&
      p.x < a.x + ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y)
    )
      inside = !inside;
  }
  return inside;
}
function intersectionTimes(a: Point, b: Point, c: Point, d: Point): number[] {
  const dx = b.x - a.x,
    dy = b.y - a.y;
  const ex = d.x - c.x,
    ey = d.y - c.y;
  const denominator = dx * ey - dy * ex;
  if (Math.abs(denominator) > EPSILON) {
    const t = ((c.x - a.x) * ey - (c.y - a.y) * ex) / denominator;
    const u = ((c.x - a.x) * dy - (c.y - a.y) * dx) / denominator;
    return t >= -EPSILON &&
      t <= 1 + EPSILON &&
      u >= -EPSILON &&
      u <= 1 + EPSILON
      ? [Math.max(0, Math.min(1, t))]
      : [];
  }
  if (Math.abs(cross(a, b, c)) > EPSILON) return [];
  const length = dx * dx + dy * dy;
  if (!length) return [];
  return [c, d]
    .map((p) => ((p.x - a.x) * dx + (p.y - a.y) * dy) / length)
    .filter((t) => t >= -EPSILON && t <= 1 + EPSILON)
    .map((t) => Math.max(0, Math.min(1, t)));
}
function segmentDistanceSquared(
  a: Point,
  b: Point,
  c: Point,
  d: Point,
): number {
  if (
    intersectionTimes(a, b, c, d).length ||
    onSegment(a, c, d) ||
    onSegment(b, c, d)
  )
    return 0;
  return Math.min(
    distanceSquared(a, c, d),
    distanceSquared(b, c, d),
    distanceSquared(c, a, b),
    distanceSquared(d, a, b),
  );
}

function uniqueEdges(edges: Segment[]): Segment[] {
  const unique = new Map<string, Segment>();
  for (const edge of edges) {
    const a = `${edge.a.x.toFixed(8)},${edge.a.y.toFixed(8)}`,
      b = `${edge.b.x.toFixed(8)},${edge.b.y.toFixed(8)}`;
    unique.set(a < b ? `${a}:${b}` : `${b}:${a}`, edge);
  }
  return [...unique.values()];
}

function splitEdge(edge: Segment, cutters: readonly Segment[]): Segment[] {
  const times = [0, 1];
  for (const other of cutters)
    if (edge !== other)
      times.push(...intersectionTimes(edge.a, edge.b, other.a, other.b));
  times.sort((a, b) => a - b);
  const at = (t: number) => ({
    x: edge.a.x + (edge.b.x - edge.a.x) * t,
    y: edge.a.y + (edge.b.y - edge.a.y) * t,
  });
  const parts: Segment[] = [];
  for (let i = 1; i < times.length; i++)
    if (times[i] - times[i - 1] > EPSILON)
      parts.push(segment(at(times[i - 1]), at(times[i])));
  return parts;
}

/** Uniform edge buckets bound checks to nearby authored boundaries, not traced artwork. */
function edgeIndex(edges: Segment[], floor: MapGeometry["floor"]) {
  // A simple hall or partition has only a handful of edges. Scanning its
  // bounds avoids creating bucket keys and a Set for every grid-neighbor test.
  // The same exact segment predicates still decide clearance afterwards.
  if (edges.length <= 8)
    return (a: Point, b: Point, margin = 0) => {
      const minX = Math.min(a.x, b.x) - margin,
        maxX = Math.max(a.x, b.x) + margin;
      const minY = Math.min(a.y, b.y) - margin,
        maxY = Math.max(a.y, b.y) + margin;
      return edges.filter(
        (edge) =>
          edge.maxX >= minX &&
          edge.minX <= maxX &&
          edge.maxY >= minY &&
          edge.minY <= maxY,
      );
    };
  const size = Math.max(4, Math.sqrt((floor.width * floor.height) / 256));
  const buckets = new Map<string, Segment[]>();
  const overflow: Segment[] = [];
  for (const edge of edges) {
    const left = Math.floor(edge.minX / size),
      right = Math.floor(edge.maxX / size);
    const top = Math.floor(edge.minY / size),
      bottom = Math.floor(edge.maxY / size);
    if ((right - left + 1) * (bottom - top + 1) > 256) {
      overflow.push(edge);
      continue;
    }
    for (let y = top; y <= bottom; y++)
      for (let x = left; x <= right; x++) {
        const key = `${x},${y}`;
        const bucket = buckets.get(key);
        if (bucket) bucket.push(edge);
        else buckets.set(key, [edge]);
      }
  }
  return (a: Point, b: Point, margin = 0) => {
    const minX = Math.min(a.x, b.x) - margin,
      maxX = Math.max(a.x, b.x) + margin;
    const minY = Math.min(a.y, b.y) - margin,
      maxY = Math.max(a.y, b.y) + margin;
    const left = Math.floor(minX / size),
      right = Math.floor(maxX / size);
    const top = Math.floor(minY / size),
      bottom = Math.floor(maxY / size);
    const overlaps = (edge: Segment) =>
      edge.maxX >= minX &&
      edge.minX <= maxX &&
      edge.maxY >= minY &&
      edge.minY <= maxY;
    if ((right - left + 1) * (bottom - top + 1) > 256)
      return edges.filter(overlaps);
    const result = new Set<Segment>(overflow.filter(overlaps));
    for (let y = top; y <= bottom; y++)
      for (let x = left; x <= right; x++) {
        for (const edge of buckets.get(`${x},${y}`) ?? [])
          if (overlaps(edge)) result.add(edge);
      }
    return [...result];
  };
}

/** Explicit circulation geometry. Decorative imported architecture is deliberately excluded. */
export function createVenueMapRoutingConstraints(geometry: MapGeometry) {
  const routing = geometry.routing;
  if (!routing || (!routing.walkableAreas.length && !routing.barriers.length))
    return undefined;
  const uniqueAreas = new Map(
    routing.walkableAreas.map((area) => [JSON.stringify(area.polygon), area]),
  );
  const areas = [...uniqueAreas.values()].map((area) => {
    const rings = area.polygon.map((ring) => ring.map(point));
    const all = rings[0];
    return {
      rings,
      minX: Math.min(...all.map((p) => p.x)),
      minY: Math.min(...all.map((p) => p.y)),
      maxX: Math.max(...all.map((p) => p.x)),
      maxY: Math.max(...all.map((p) => p.y)),
    };
  });
  const areaEdges = uniqueEdges(
    areas.flatMap(({ rings }) =>
      rings.flatMap((ring) =>
        ring.map((p, i) => segment(p, ring[(i + 1) % ring.length])),
      ),
    ),
  );
  const boundaryEdges = edgeIndex(areaEdges, geometry.floor);
  const boothPolygons = new Map(
    geometry.stands.map((stand) => [
      stand.id,
      stand.polygons.map((polygon) => polygon.map(point)),
    ]),
  );
  const doors = routing.doors.filter((door) => !door.closed);
  const walls = routing.barriers.flatMap((barrier) => {
    const a = point(barrier.start),
      b = point(barrier.end);
    const at = (t: number): Point => ({
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
    });
    const gaps = doors
      .filter((door) => door.barrierId === barrier.id)
      .sort((a, b) => a.start - b.start);
    const parts: Segment[] = [];
    let cursor = 0;
    for (const gap of gaps) {
      if (gap.start > cursor + EPSILON)
        parts.push(segment(at(cursor), at(gap.start)));
      cursor = Math.max(cursor, gap.end);
    }
    if (cursor < 1 - EPSILON) parts.push(segment(at(cursor), b));
    return parts;
  });
  const wallEdges = edgeIndex(uniqueEdges(walls), geometry.floor);
  // Grid rows and endpoint coordinates recur across neighboring segments.
  // Numeric keys retain exact coordinates without allocating a string on
  // every lookup. The total point budget remains shared across all rows.
  const membership = new Map<number, Map<number, boolean>>();
  let membershipCount = 0;
  let unionBoundary: ReturnType<typeof edgeIndex> | undefined;
  function walkable(p: Point): boolean {
    if (!areas.length) return true;
    let row = membership.get(p.y);
    const cached = row?.get(p.x);
    if (cached !== undefined) return cached;
    let inside = false;
    if (unionBoundary) {
      // After preprocessing, membership depends only on the union outline,
      // never on overlapping-area order or repeated source vertices.
      const right = { x: geometry.floor.x + geometry.floor.width + 1, y: p.y };
      for (const edge of unionBoundary(p, right)) {
        const { a, b } = edge;
        if (onSegment(p, a, b)) {
          inside = true;
          break;
        }
        if (
          a.y > p.y !== b.y > p.y &&
          p.x < a.x + ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y)
        )
          inside = !inside;
      }
    } else {
      inside = areas.some(
        (area) =>
          p.x >= area.minX - EPSILON &&
          p.x <= area.maxX + EPSILON &&
          p.y >= area.minY - EPSILON &&
          p.y <= area.maxY + EPSILON &&
          inRing(p, area.rings[0], true) &&
          !area.rings.slice(1).some((hole) => inRing(p, hole, false)),
      );
    }
    // Grid endpoints repeat across neighbor edges and route requests. Bound
    // retained membership data alongside the existing bounded route cache.
    // Arbitrary smoothing points can have distinct rows; cap their Map overhead
    // as well as the point count. Uncached points still run the exact query.
    if (membershipCount < 160_000 && (row || membership.size < 4_096)) {
      if (!row) {
        row = new Map();
        membership.set(p.y, row);
      }
      row.set(p.x, inside);
      membershipCount++;
    }
    return inside;
  }
  const allowed = (p: Point, enteredStand?: string): boolean =>
    walkable(p) ||
    (!!enteredStand &&
      !!boothPolygons
        .get(enteredStand)
        ?.some((polygon) => inRing(p, polygon, true)));

  // Only the exterior of the area union is an obstacle: shared edges and
  // overlapping areas remain connected. Splitting before classification also
  // preserves short openings and holes at concave/intersecting boundaries.
  const exposedBoundaries = uniqueEdges(
    areaEdges.flatMap((edge) =>
      splitEdge(edge, boundaryEdges(edge.a, edge.b)).filter((part) => {
        const dx = part.b.x - part.a.x,
          dy = part.b.y - part.a.y;
        const length = Math.hypot(dx, dy);
        const center = {
          x: (part.a.x + part.b.x) / 2,
          y: (part.a.y + part.b.y) / 2,
        };
        const epsilon = Math.min(1e-6, length / 100);
        const normal = {
          x: (-dy / length) * epsilon,
          y: (dx / length) * epsilon,
        };
        return (
          allowed({ x: center.x + normal.x, y: center.y + normal.y }) !==
          allowed({ x: center.x - normal.x, y: center.y - normal.y })
        );
      }),
    ),
  );
  const exposedEdges = edgeIndex(exposedBoundaries, geometry.floor);
  unionBoundary = exposedEdges;
  membership.clear();
  membershipCount = 0;
  const boothBoundaryParts = new Map<string, ReturnType<typeof edgeIndex>>();
  function boundariesFor(enteredStand?: string) {
    const polygons = enteredStand ? boothPolygons.get(enteredStand) : undefined;
    if (!enteredStand || !polygons?.length) return exposedEdges;
    let index = boothBoundaryParts.get(enteredStand);
    if (!index) {
      const cutters = polygons.flatMap((polygon) =>
        polygon.map((p, i) => segment(p, polygon[(i + 1) % polygon.length])),
      );
      const cutterEdges = edgeIndex(cutters, geometry.floor);
      const outside = exposedBoundaries.flatMap((edge) =>
        splitEdge(edge, cutterEdges(edge.a, edge.b)).filter((part) => {
          const center = {
            x: (part.a.x + part.b.x) / 2,
            y: (part.a.y + part.b.y) / 2,
          };
          return !polygons.some((polygon) => inRing(center, polygon, true));
        }),
      );
      index = edgeIndex(outside, geometry.floor);
      boothBoundaryParts.set(enteredStand, index);
    }
    return index;
  }

  function clearSegment(
    a: Point,
    b: Point,
    clearance: number,
    enteredStand?: string,
  ): boolean {
    if (!allowed(a, enteredStand) || !allowed(b, enteredStand)) return false;
    for (const wall of wallEdges(a, b, clearance)) {
      if (
        segmentDistanceSquared(a, b, wall.a, wall.b) <
        clearance ** 2 - EPSILON
      )
        return false;
    }
    if (!areas.length) return true;
    for (const edge of boundariesFor(enteredStand)(a, b, clearance)) {
      if (
        segmentDistanceSquared(a, b, edge.a, edge.b) <
        clearance ** 2 - EPSILON
      )
        return false;
    }
    const edges = exposedEdges(a, b);
    // The endpoint connector may leave only its own booth footprint, never
    // bypass a wall or cross a disconnected region of the authored floor.
    if (enteredStand)
      for (const polygon of boothPolygons.get(enteredStand) ?? [])
        for (let i = 0; i < polygon.length; i++)
          edges.push(segment(polygon[i], polygon[(i + 1) % polygon.length]));
    const times = [0, 1];
    for (const edge of edges)
      times.push(...intersectionTimes(a, b, edge.a, edge.b));
    if (times.length === 2) return true;
    times.sort((a, b) => a - b);
    // Checking each boundary-delimited interval is exact even for holes,
    // concave rings and overlapping walkable areas. Endpoint checks alone are not.
    for (let i = 1; i < times.length; i++) {
      if (times[i] - times[i - 1] <= EPSILON) continue;
      const t = (times[i] + times[i - 1]) / 2;
      if (
        !allowed(
          { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t },
          enteredStand,
        )
      )
        return false;
    }
    return true;
  }

  function clearTriangle(
    a: Point,
    b: Point,
    c: Point,
    clearance: number,
  ): boolean {
    if (
      !clearSegment(a, b, clearance) ||
      !clearSegment(b, c, clearance) ||
      !clearSegment(c, a, clearance)
    )
      return false;
    const triangle = [a, b, c];
    const min = { x: Math.min(a.x, b.x, c.x), y: Math.min(a.y, b.y, c.y) };
    const max = { x: Math.max(a.x, b.x, c.x), y: Math.max(a.y, b.y, c.y) };
    // A curve lies inside its control triangle. Reject enclosed walls or hole
    // boundaries as well as edge crossings. Internal union boundaries were
    // removed above and therefore do not prevent a safe rounded bend.
    return ![...wallEdges(min, max, clearance), ...exposedEdges(min, max)].some(
      (edge) =>
        inRing(edge.a, triangle, false) || inRing(edge.b, triangle, false),
    );
  }

  function doorways(clearance: number, gridStep: number): Point[][] {
    const barriers = new Map(
      routing!.barriers.map((barrier) => [barrier.id, barrier]),
    );
    return doors.flatMap((door) => {
      const barrier = barriers.get(door.barrierId);
      if (!barrier) return [];
      const [ax, ay] = barrier.start,
        [bx, by] = barrier.end;
      const dx = bx - ax,
        dy = by - ay,
        length = Math.hypot(dx, dy);
      if ((door.end - door.start) * length < clearance * 2 - EPSILON) return [];
      const t = (door.start + door.end) / 2;
      const center = { x: ax + dx * t, y: ay + dy * t };
      const inset = clearance + gridStep;
      const offset = { x: (-dy / length) * inset, y: (dx / length) * inset };
      // Exact door centers prevent a narrow valid opening from disappearing
      // merely because it falls between two routing-grid columns.
      return [
        [
          { x: center.x - offset.x, y: center.y - offset.y },
          center,
          { x: center.x + offset.x, y: center.y + offset.y },
        ],
      ];
    });
  }
  return { clearSegment, clearTriangle, doorways };
}
